import React, { Fragment, useEffect, useMemo, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { useNavigate } from "react-router-dom"
import { Check, Clock } from "lucide-react"

import { supabase } from "../lib/supabaseClient"
import { NiyyahLogo } from "../components/intake/dashboard/NiyyahLogo"
import { IntroductionStatus } from "../components/intake/dashboard/IntroductionStatus"
import { AlignmentSnapshot } from "../components/intake/dashboard/AlignmentSnapshot"
import { MatchIntroduction } from "../components/intake/dashboard/MatchIntroduction"

type DashboardMode = "full" | "teaser" | "blocked"

type DashboardState =
  | "waiting"
  | "match_ready"
  | "multiple_matches"
  | "viewing_match"
  | "pending"
  | "accepted"

type MatchCard = {
  matchId: string
  otherMemberId: string

  // What your existing MatchIntroduction UI expects
  name: string
  age: number
  location: string
  occupation?: string
  compatibility: string
  summary?: string
  previouslyMarried?: boolean
  hasChildren?: boolean

  receivedDateLabel?: string
  status: number

  // who am I in this match?
  myRole: "a" | "b"
  myDecision: number // 0 pending, 1 accept, 2 decline
  otherDecision: number
}

type PendingIntro = {
  name: string
  age: number
  location: string
  compatibility: string
  receivedDate: string
  matchId: string
}[]

function DesktopHeaderSpine() {
  const stages = [
    { id: "intent", label: "Intention", status: "completed" as const },
    { id: "values", label: "Values", status: "completed" as const },
    { id: "lifestyle", label: "Lifestyle", status: "completed" as const },
    { id: "communication", label: "Communication", status: "completed" as const },
    { id: "identity", label: "Identity", status: "completed" as const },
    { id: "introduction", label: "Introduction", status: "active" as const },
  ]

  return (
    <nav className="hidden md:flex items-center gap-1" aria-label="Journey progress">
      {stages.map((stage, index) => (
        <Fragment key={stage.id}>
          <div className="flex items-center gap-1.5">
            <div className="relative">
              {stage.status === "active" && (
                <motion.div
                  className="absolute inset-0 rounded-full bg-niyyah-purple/30"
                  animate={{ scale: [1, 1.6, 1], opacity: [0.4, 0, 0.4] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                />
              )}
              {stage.status === "completed" ? (
                <Check className="w-3 h-3 text-niyyah-light" strokeWidth={3} />
              ) : (
                <div className="w-3 h-3 rounded-full bg-niyyah-purple shadow-[0_0_12px_rgba(155,135,199,0.5)]" />
              )}
            </div>
            <span
              className={`text-[11px] tracking-wide whitespace-nowrap ${
                stage.status === "completed"
                  ? "text-niyyah-light/70 font-light"
                  : "text-niyyah-purple font-medium"
              }`}
            >
              {stage.label}
            </span>
          </div>

          {index < stages.length - 1 && (
            <div
              className={`w-4 lg:w-6 h-px mx-1 ${
                stage.status === "completed" ? "bg-niyyah-light/30" : "bg-gray-100"
              }`}
            />
          )}
        </Fragment>
      ))}
    </nav>
  )
}

function MobileJourneySpine() {
  const stages = [
    { id: "intent", label: "Intent", status: "completed" as const },
    { id: "values", label: "Values", status: "completed" as const },
    { id: "lifestyle", label: "Lifestyle", status: "completed" as const },
    { id: "communication", label: "Communication", status: "completed" as const },
    { id: "identity", label: "Identity", status: "completed" as const },
    { id: "introduction", label: "Introduction", status: "active" as const },
  ]

  return (
    <nav className="flex items-start justify-between w-full" aria-label="Journey progress">
      {stages.map((stage, index) => (
        <Fragment key={stage.id}>
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-center gap-1.5 flex-shrink-0"
          >
            <div className="relative">
              {stage.status === "active" && (
                <motion.div
                  className="absolute inset-0 rounded-full bg-niyyah-purple/30"
                  animate={{ scale: [1, 1.8, 1], opacity: [0.4, 0, 0.4] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                />
              )}
              {stage.status === "completed" ? (
                <div className="w-4 h-4 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-niyyah-light" strokeWidth={3} />
                </div>
              ) : (
                <div className="w-4 h-4 rounded-full bg-niyyah-purple shadow-[0_0_12px_rgba(155,135,199,0.5)]" />
              )}
            </div>
            <span
              className={`text-[10px] tracking-wide text-center leading-tight ${
                stage.status === "completed"
                  ? "text-niyyah-light/80 font-light"
                  : "text-niyyah-purple font-medium"
              }`}
            >
              {stage.label}
            </span>
          </motion.div>

          {index < stages.length - 1 && (
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.3, delay: index * 0.05 + 0.1, ease: [0.22, 1, 0.36, 1] }}
              className={`flex-1 h-px mt-2 origin-left min-w-[8px] ${
                stage.status === "completed" ? "bg-niyyah-light/30" : "bg-gray-100"
              }`}
            />
          )}
        </Fragment>
      ))}
    </nav>
  )
}

function PendingIntroductions({
  introductions,
  onViewIntro,
}: {
  introductions: PendingIntro
  onViewIntro: (matchId: string) => void
}) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div className="flex items-center gap-2 mb-6">
          <Clock className="w-4 h-4 text-niyyah-purple/60" />
          <h2 className="text-sm font-medium text-niyyah-text">Pending Introductions</h2>
          <span className="ml-auto text-xs text-niyyah-muted bg-niyyah-pale px-2 py-0.5 rounded-full">
            {introductions.length}
          </span>
        </div>

        <div className="space-y-3">
          {introductions.map((intro, index) => (
            <motion.div
              key={intro.matchId}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="bg-niyyah-pale/30 border border-niyyah-light/20 rounded-xl p-4 cursor-pointer hover:border-niyyah-light/40 transition-colors"
              onClick={() => onViewIntro(intro.matchId)}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-niyyah-light/30 flex items-center justify-center text-lg font-light text-niyyah-purple">
                  {intro.name.charAt(0)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-niyyah-text">
                      {intro.name}, {intro.age}
                    </span>
                    <span className="text-xs text-niyyah-purple bg-niyyah-pale px-2 py-0.5 rounded-full">
                      {intro.compatibility}
                    </span>
                  </div>
                  <p className="text-xs text-niyyah-muted font-light">{intro.location}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-niyyah-muted/60 font-light">{intro.receivedDate}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="text-xs text-niyyah-muted/60 font-light text-center mt-6">
          Tap to review and respond
        </p>
      </motion.div>
    </div>
  )
}

function timeAgoLabel(ts: string | null | undefined) {
  if (!ts) return "Recently"
  const d = new Date(ts)
  const diff = Date.now() - d.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `${mins} min ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`
  const days = Math.floor(hrs / 24)
  return `${days} day${days === 1 ? "" : "s"} ago`
}

export function MemberDashboard({ mode }: { mode: DashboardMode }) {
  const navigate = useNavigate()

  const isPaid = mode === "full"
  const isTeaser = mode === "teaser"
  const isBlocked = mode === "blocked"
  const DEV = import.meta.env.DEV

  const goToUpgrade = () => navigate("/pricing?step=payment&plan=active")
  const goToIntake = () => navigate("/", { replace: true })
  const goToSettings = () => navigate("/settings" + window.location.search)

  const [loading, setLoading] = useState(true)
  const [fatalError, setFatalError] = useState<string>("")

  const [myMemberId, setMyMemberId] = useState<string>("")
  const [userEmail, setUserEmail] = useState<string>("")

  const [pendingCards, setPendingCards] = useState<MatchCard[]>([])
  const [acceptedCards, setAcceptedCards] = useState<MatchCard[]>([])

  const [dashboardState, setDashboardState] = useState<DashboardState>("waiting")
  const [activeMatchId, setActiveMatchId] = useState<string>("")

  const activeCard = useMemo(
    () => pendingCards.concat(acceptedCards).find((c) => c.matchId === activeMatchId) || null,
    [pendingCards, acceptedCards, activeMatchId],
  )

  // Pull logged-in email (source of truth)
useEffect(() => {
  let cancelled = false

  const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string
  const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string

  async function callFn(
    name: string,
    jwt: string,
    method: "GET" | "POST" = "POST",
    body?: any,
  ) {
    if (!SUPABASE_URL) throw new Error("Missing VITE_SUPABASE_URL")
    if (!SUPABASE_ANON) throw new Error("Missing VITE_SUPABASE_ANON_KEY")

    const url = `${SUPABASE_URL}/functions/v1/${name}`

    const res = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${jwt}`,
        apikey: SUPABASE_ANON,
        "Content-Type": "application/json",
      },
      ...(method !== "GET" ? { body: JSON.stringify(body ?? {}) } : {}),
    })

    const text = await res.text()
    let json: any = null
    try {
      json = JSON.parse(text)
    } catch {}

    if (!res.ok) {
      throw new Error(`Fn ${name} failed ${res.status}: ${text}`)
    }
    return json
  }

  const run = async () => {
    try {
      setLoading(true)
      setFatalError("")

      // 1) read whatever is currently persisted (may be stale)
      const { data: before, error: beforeErr } = await supabase.auth.getSession()
      if (beforeErr) throw beforeErr

      // 2) force-refresh (uses refresh token under the hood if available)
      const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession()
      if (refreshErr) throw refreshErr

      // 3) prefer refreshed session, fall back to before
      const session = refreshed.session ?? before.session
      if (!session?.access_token) throw new Error("No access token after refresh")

      // pull user email (optional, but fine to keep)
      const { data: u } = await supabase.auth.getUser()
      setUserEmail((u.user?.email || "").toLowerCase())

      const jwt = session.access_token

      // ✅ JWT debug (safe to remove after)
      try {
        const token = session.access_token
        const base64Url = token.split(".")[1]
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join(""),
        )
        const payload = JSON.parse(jsonPayload)

        console.log("JWT iss:", payload.iss)
        console.log("JWT aud:", payload.aud)
        console.log("JWT exp:", payload.exp)
        console.log("JWT sub:", payload.sub)
      } catch (e) {
        console.log("JWT decode failed", e)
      }

      // 0) ensure member is linked
      await callFn("member-link-auth", jwt, "POST", {})

      // 1) load matches
      const payload = await callFn("match-list", jwt, "GET")

      if (cancelled) return

      setMyMemberId(payload?.member_id || "")

      // 2) Map Edge rows → your MatchCard shape
      const toCard = (r: any): MatchCard => {
        const p = r.other_profile || {}
        const name = String(p.first_name || "Someone new").trim()
        const age = typeof p.age === "number" ? p.age : 0
        const location = String(p.city || "—").trim()

        return {
          matchId: r.id,
          otherMemberId: r.other_member_id,

          name,
          age,
          location,
          occupation: undefined,

          // if compatibility_score is 0-1 or 0-100 depends on your schema.
          // If yours is 0..1, use Math.round(score * 100).
          compatibility:
            r.compatibility_score != null ? `${Math.round(r.compatibility_score)}%` : "—",

          summary: undefined,
          previouslyMarried:
            typeof p.previously_married === "boolean" ? p.previously_married : undefined,
          hasChildren: typeof p.has_children === "boolean" ? p.has_children : undefined,

          receivedDateLabel: timeAgoLabel(r.created_at),

          // keep your status field for now (you can refine later)
          status: 1,

          myRole: r.i_am_member_a ? "a" : "b",
          myDecision: r.my_decision ?? 0,
          otherDecision: r.their_decision ?? 0,
        }
      }

      const pending = (payload?.pending ?? []).map(toCard)
      const accepted = (payload?.accepted ?? []).map(toCard)

      setPendingCards(pending)
      setAcceptedCards(accepted)

      if (pending.length > 0) setDashboardState("pending")
      else if (accepted.length > 0) setDashboardState("accepted")
      else setDashboardState("waiting")

      setLoading(false)
    } catch (e: any) {
      if (cancelled) return
      setLoading(false)
      setFatalError(e?.message || "Something went wrong loading your dashboard.")
    }
  }

  // initial load
  run()

  // subscribe outside run(), cleanup returned from useEffect
  const { data: sub } = supabase.auth.onAuthStateChange(() => {
    run()
  })

  return () => {
    cancelled = true
    sub?.subscription?.unsubscribe()
  }
}, [navigate])

  const userName = userEmail && userEmail.includes("@") ? userEmail.split("@")[0] : userEmail
  const userInitial = (userName?.charAt(0) || "U").toUpperCase()

  const handleLogout = async () => {
    if (!window.confirm("Are you sure you want to logout?")) return
    try {
      await supabase.auth.signOut()
    } catch {}

    localStorage.removeItem("niyyah_email")
    localStorage.removeItem("niyyah_intake_complete")
    localStorage.removeItem("niyyah_plan")
    localStorage.removeItem("niyyah_resume")
    localStorage.removeItem("niyyah_profile_picture")

    navigate("/login", { replace: true })
  }

  const pendingIntroductions: PendingIntro = useMemo(() => {
    return pendingCards.map((c) => ({
      name: c.name,
      age: c.age || 0,
      location: c.location,
      compatibility: c.compatibility,
      receivedDate: c.receivedDateLabel || "Recently",
      matchId: c.matchId,
    }))
  }, [pendingCards])

  const openMatch = (matchId: string) => {
    if (!isPaid) return
    setActiveMatchId(matchId)
    setDashboardState("viewing_match")
  }

  const goBackToList = () => {
    setActiveMatchId("")
    if (pendingCards.length > 0) setDashboardState("pending")
    else if (acceptedCards.length > 0) setDashboardState("accepted")
    else setDashboardState("waiting")
  }

  const updateDecision = async (matchId: string, decision: 1 | 2) => {
    const card = pendingCards.find((c) => c.matchId === matchId) || activeCard
    if (!card) return

    // Update the correct decision column
    const isA = card.myRole === "a"
    const update: any = isA
      ? { member_a_decision: decision, member_a_decided_at: new Date().toISOString() }
      : { member_b_decision: decision, member_b_decided_at: new Date().toISOString() }

    // Roll up status: decline is terminal; accept only becomes accepted if other already accepted
    let nextStatus: number | null = null
    if (decision === 2) {
      nextStatus = 3 // declined
    } else if (decision === 1) {
      if (card.otherDecision === 1) nextStatus = 2 // accepted
      else nextStatus = 1 // still proposed
    }

    if (nextStatus != null) update.status = nextStatus

    const { error } = await supabase.from("matches").update(update).eq("id", matchId)
    if (error) {
      // eslint-disable-next-line no-alert
      alert(error.message)
      return
    }

    // Refresh by reloading page state (simple + reliable for now)
    window.location.reload()
  }

  const renderMainContent = () => {
    if (loading) {
      return (
        <div className="text-center text-sm text-niyyah-muted font-light py-20">
          Loading…
        </div>
      )
    }

    if (fatalError) {
      return (
        <div className="text-center text-sm text-red-500 font-light py-20">
          {fatalError}
        </div>
      )
    }

    // If viewing match, render your existing match UI with real data
    if (dashboardState === "viewing_match" && activeCard) {
      return (
        <MatchIntroduction
          key="match-card"
          match={{
            name: activeCard.name,
            age: activeCard.age,
            location: activeCard.location,
            occupation: activeCard.occupation,
            compatibility: activeCard.compatibility,
            summary: activeCard.summary,
            previouslyMarried: activeCard.previouslyMarried,
            hasChildren: activeCard.hasChildren,
          }}
          onAccept={() => updateDecision(activeCard.matchId, 1)}
          onDecline={() => updateDecision(activeCard.matchId, 2)}
          onBack={goBackToList}
        />
      )
    }

    // Pending section list
    if (pendingIntroductions.length > 0) {
      return (
        <PendingIntroductions
          key="pending"
          introductions={pendingIntroductions}
          onViewIntro={(matchId) => openMatch(matchId)}
        />
      )
    }

    // Accepted section (simple list for now)
    if (acceptedCards.length > 0) {
      return (
        <div className="w-full max-w-md mx-auto px-6 py-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-sm font-medium text-niyyah-text">Accepted Matches</h2>
            <span className="text-xs text-niyyah-muted bg-niyyah-pale px-2 py-0.5 rounded-full">
              {acceptedCards.length}
            </span>
          </div>

          <div className="space-y-3">
            {acceptedCards.map((c) => (
              <button
                key={c.matchId}
                onClick={() => openMatch(c.matchId)}
                className="w-full text-left bg-white border border-gray-100 rounded-xl p-4 hover:border-niyyah-light/40 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-niyyah-pale flex items-center justify-center text-lg font-light text-niyyah-purple">
                    {c.name.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-niyyah-text">
                        {c.name}{c.age ? `, ${c.age}` : ""}
                      </span>
                      <span className="text-xs text-niyyah-purple bg-niyyah-pale px-2 py-0.5 rounded-full">
                        {c.compatibility}
                      </span>
                    </div>
                    <p className="text-xs text-niyyah-muted font-light">{c.location}</p>
                  </div>
                  <p className="text-[10px] text-niyyah-muted/60 font-light">
                    {c.receivedDateLabel || "—"}
                  </p>
                </div>
              </button>
            ))}
          </div>

          <p className="text-xs text-niyyah-muted/60 font-light text-center mt-6">
            Chat comes next (after we add the conversation tables).
          </p>
        </div>
      )
    }

    // Nothing to show yet
    return (
      <IntroductionStatus
        key="status"
        status="waiting"
        count={1}
        onView={() => {}}
      />
    )
  }

  // ---- Blur rules ----
  const blurAll = isBlocked
  const blurPremium = isTeaser
  const lockedClass = "pointer-events-none select-none"

  const isViewingMatch = dashboardState === "viewing_match"

  return (
    <div className="min-h-screen bg-white w-full relative">
      <AnimatePresence>
        {isBlocked && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-6"
          >
            <div className="absolute inset-0 bg-white/60 backdrop-blur-md" />
            <div className="relative z-10 w-full max-w-lg rounded-3xl border border-gray-200 bg-white p-8 text-center shadow-xl">
              <h2 className="text-2xl md:text-3xl font-light text-niyyah-text mb-3">
                Complete your intake
              </h2>
              <p className="text-sm md:text-base text-niyyah-muted font-light leading-relaxed mb-6">
                This helps us understand your values and preferences so we can match you properly.
              </p>
              <button onClick={goToIntake} className="feedback-button-circle feedback-button-circle--md mx-auto">
                <span className="text-sm font-light leading-tight">
                  Start
                  <br />
                  intake
                </span>
              </button>
            </div>
          </motion.div>
        )}

        {isTeaser && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="fixed bottom-6 left-0 right-0 z-[60] px-6"
          >
            <div className="mx-auto max-w-2xl rounded-3xl border border-gray-200 bg-white/90 backdrop-blur-md p-5 shadow-lg flex items-center justify-between gap-4">
              <p className="text-sm md:text-base text-niyyah-text font-light">
                Unlock curated matches and full access.
              </p>
              <button onClick={goToUpgrade} className="feedback-button-circle feedback-button-circle--sm shrink-0">
                <span className="text-sm font-light leading-tight">Upgrade</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={blurAll ? "blur-md pointer-events-none select-none" : ""}>
        <motion.header
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full px-4 py-6 md:px-12 md:py-8"
        >
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
            <NiyyahLogo />
            <DesktopHeaderSpine />

            <div className="flex items-center gap-3">
              <div className="relative group">
                <button
                  onClick={goToSettings}
                  className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-niyyah-pale flex items-center justify-center text-niyyah-purple font-light text-sm border border-niyyah-light/20 hover:border-niyyah-light/40 transition-all duration-300 hover:scale-105"
                  aria-label="Profile settings"
                  title={userEmail || "Profile settings"}
                >
                  {userInitial}
                </button>
                <div className="absolute top-full right-0 mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  <div className="bg-niyyah-text text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap font-light">
                    Profile settings
                  </div>
                </div>
              </div>

              <button
                onClick={handleLogout}
                className="text-xs text-niyyah-muted hover:text-niyyah-purple transition-colors font-light"
              >
                Log out
              </button>
            </div>
          </div>
        </motion.header>

        <div className="max-w-6xl mx-auto px-4 md:px-12">
          <div className="border-b border-gray-100" />
        </div>

        {!isViewingMatch && (
          <>
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="md:hidden px-4 py-4"
            >
              <MobileJourneySpine />
            </motion.div>

            <div className="md:hidden max-w-6xl mx-auto px-4">
              <div className="border-b border-gray-100" />
            </div>
          </>
        )}

        <main className="w-full max-w-6xl mx-auto px-4 md:px-12 pb-16">
          <div className="hidden md:grid md:grid-cols-[1fr_220px] md:gap-8 lg:gap-12 min-h-[calc(100vh-200px)]">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className={`flex items-center justify-center relative ${isViewingMatch ? "col-span-2" : ""} ${
                blurPremium ? `blur-md ${lockedClass}` : ""
              }`}
            >
              <AnimatePresence mode="wait">{renderMainContent()}</AnimatePresence>
            </motion.div>

            {!isViewingMatch && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className={`pt-6 ${blurPremium ? `blur-md ${lockedClass}` : ""}`}
              >
                <div className="border-l border-gray-100 h-full pl-6 lg:pl-8 -ml-4 lg:-ml-6">
                  <AlignmentSnapshot />
                </div>
              </motion.div>
            )}
          </div>

          <div className="md:hidden space-y-8 pt-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className={blurPremium ? `blur-md ${lockedClass}` : ""}
            >
              <AnimatePresence mode="wait">{renderMainContent()}</AnimatePresence>
            </motion.div>

            {!isViewingMatch && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className={blurPremium ? `blur-md ${lockedClass}` : ""}
              >
                <div className="border-t border-gray-100 mb-6" />
                <AlignmentSnapshot />
              </motion.div>
            )}
          </div>
        </main>

        <footer className="w-full py-8 text-center border-t border-gray-50 mt-auto">
          <p className="text-xs text-niyyah-muted/40 font-light mb-4">© 2026 Niyyah</p>

          {DEV && isPaid && (
            <button
              onClick={() => window.location.reload()}
              className="text-[10px] uppercase tracking-widest text-niyyah-purple/60 hover:text-niyyah-purple transition-colors font-medium border border-niyyah-purple/20 px-3 py-1 rounded-full"
            >
              DEV: Refresh
            </button>
          )}
        </footer>
      </div>

      <style>{`
        .feedback-button-circle {
          position: relative;
          width: 100px;
          height: 100px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          transition: transform 0.3s ease;
          overflow: hidden;
          background: #9b8fc4;
          border: none;
        }
        .feedback-button-circle--md { width: 112px; height: 112px; }
        .feedback-button-circle--sm { width: 92px; height: 92px; }
        .feedback-button-circle::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
          background-size: 200px 200px;
          opacity: 0.5;
          mix-blend-mode: overlay;
          pointer-events: none;
          z-index: 1;
        }
        .feedback-button-circle span { position: relative; z-index: 2; }
        .feedback-button-circle:hover { transform: scale(1.05); }
      `}</style>
    </div>
  )
}